/*
 * WD_GEOM — pure geometry for the Wardrobe Designer. No DOM, no prices.
 *
 * Ported from wardrobe-crm's doorCalc()/SECMETA/drawElev() maths, with every
 * dollar figure left behind. Door panel geometry is intentionally self-contained
 * (its own DOOR_GEOM table, matching WD_CATALOGUE.doors.types[].geom) so this
 * file has no hard dependency on the catalogue and can run standalone.
 *
 * Vanilla ES2020, no build step. Exposes window.WD_GEOM only.
 */
(function () {
  'use strict';

  var GUARD_MAX = 200;

  /* Sliding panel geometry. hinged/bifold remain only so the shared CRM
     contract keeps its shape; no UI can select them. The non-geometry fields on
     wardrobe-crm's equivalent table are deliberately not reproduced here. */
  var DOOR_GEOM = {
    sliding: { overlap: 30, topTrack: 35, botTrack: 10, maxPanel: 1250, minPanel: 600 },
    hinged: { gap: 4, heightDeduct: 18, maxPanel: 600, minPanel: 350 },
    bifold: { gap: 4, heightDeduct: 18, maxPanel: 500, minPanel: 300 }
  };

  var SECTION_NOUNS = {
    drawer: 'drawers',
    shelf: 'shelves',
    single: 'single hanging',
    double: 'double hanging'
  };

  function num(v, fallback) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    return typeof n === 'number' && isFinite(n) ? n : fallback;
  }

  function findOption(list, value) {
    if (!Array.isArray(list)) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].value === value) return list[i];
    }
    return null;
  }

  /* ---------------------------------------------------------------------
     doorGeom(W, H, type) -> { n, pw, ph, outOfRange }
     Faithful port of doorCalc(): auto panel count, then nudge the count until
     the panel width sits inside [minPanel, maxPanel], or flag outOfRange.
     Never returns a customer-facing warning string.
  --------------------------------------------------------------------- */
  /* Panel count for a width, per the live product catalogue's Size option. */
  function bandCount(W) {
    W = num(W, 0);
    if (W <= 2400) return 2;
    if (W <= 3600) return 3;
    return 4;
  }

  /* Which counts we can actually build at this width: any whose panel width
     lands inside minPanel..maxPanel. Always includes the catalogue default. */
  function allowedDoorCounts(W, type) {
    W = num(W, 0);
    var cfg = DOOR_GEOM[type || 'sliding'];
    if (!cfg) return [bandCount(W)];
    var out = [];
    [2, 3, 4].forEach(function (n) {
      var pw = (W + cfg.overlap * (n - 1)) / n;
      if (pw >= cfg.minPanel && pw <= cfg.maxPanel) out.push(n);
    });
    if (!out.length) out.push(bandCount(W));
    return out;
  }

  function doorGeom(W, H, type, preferredCount) {
    W = num(W, 0);
    H = num(H, 0);

    if (!type || type === 'none') {
      return { n: 0, pw: 0, ph: 0, outOfRange: false };
    }

    var cfg = DOOR_GEOM[type];
    if (!cfg) {
      return { n: 0, pw: 0, ph: 0, outOfRange: true };
    }

    var isSliding = type === 'sliding';
    /* A customer-chosen panel count wins, as long as it survives the range
       clamp below. With none supplied, fall back to the width bands the live
       product catalogue uses (2 doors to 2400, 3 to 3600, 4 to 4800). */
    var n = num(preferredCount, 0);
    if (!n) {
      n = isSliding
        ? bandCount(W)
        : Math.max(2, Math.ceil(W / 560));
    }
    n = Math.max(2, Math.round(n));

    function compute(count) {
      if (isSliding) {
        return {
          pw: (W + cfg.overlap * (count - 1)) / count,
          ph: H - cfg.topTrack - cfg.botTrack
        };
      }
      return {
        pw: (W - cfg.gap * (count + 1)) / count,
        ph: H - cfg.heightDeduct
      };
    }

    var res = compute(n);
    var guard = 0;
    while (res.pw > cfg.maxPanel && guard < GUARD_MAX) {
      n += 1;
      res = compute(n);
      guard += 1;
    }
    guard = 0;
    while (res.pw < cfg.minPanel && n > 2 && guard < GUARD_MAX) {
      n -= 1;
      res = compute(n);
      guard += 1;
    }

    var outOfRange = res.pw > cfg.maxPanel || res.pw < cfg.minPanel;
    return { n: n, pw: res.pw, ph: res.ph, outOfRange: outOfRange };
  }

  /* ---------------------------------------------------------------------
     Section factory — CRM-compatible keys (t, w, drawers, shelves, hangAbove),
     defaults sourced from the catalogue.
  --------------------------------------------------------------------- */
  function sectionDefaults(type, cat) {
    var meta = cat && cat.sections && cat.sections[type];
    var sec = { t: type, w: meta ? meta.def : 0 };
    if (type === 'drawer') {
      var drawersParam = meta && meta.params && meta.params.drawers;
      var hangAboveParam = meta && meta.params && meta.params.hangAbove;
      sec.drawers = (drawersParam && typeof drawersParam.default === 'number') ? drawersParam.default : 4;
      sec.hangAbove = hangAboveParam ? hangAboveParam.default !== false : true;
    } else if (type === 'shelf') {
      var shelvesParam = meta && meta.params && meta.params.shelves;
      sec.shelves = (shelvesParam && typeof shelvesParam.default === 'number') ? shelvesParam.default : 5;
    }
    return sec;
  }

  /* ---------------------------------------------------------------------
     realiseLayout(recipe, W, cat) -> sections[]
     Implements the plan's fill algorithm: fixed bays take their catalogue
     default width; the (single) flexible bay in the recipe is duplicated
     until each copy fits under its catalogue max, split evenly, rounded to
     5mm, with any rounding remainder added to the last flexible section so
     the total ALWAYS sums to W exactly.
  --------------------------------------------------------------------- */
  function realiseLayout(recipe, W, cat) {
    W = num(W, 0);
    var secs = [];
    if (!Array.isArray(recipe) || !recipe.length || !cat || !cat.sections) return secs;

    var flexTokenIndex = -1;
    var flexType = null;
    var fixed = 0;
    var i;

    for (i = 0; i < recipe.length; i++) {
      var token = recipe[i];
      if (typeof token !== 'string' || !token.length) continue;
      if (token.charAt(0) === '*') {
        if (flexTokenIndex === -1) {
          flexTokenIndex = i;
          flexType = token.slice(1);
        }
      } else {
        var fixedMeta = cat.sections[token];
        fixed += fixedMeta ? fixedMeta.def : 0;
      }
    }

    if (flexTokenIndex === -1) {
      /* No flexible bay in this recipe: every bay is fixed width. Still
         guaranteed to sum to W exactly by pushing any shortfall/excess onto
         the final section. */
      for (i = 0; i < recipe.length; i++) {
        var fixedToken = recipe[i];
        if (typeof fixedToken === 'string' && fixedToken.length && fixedToken.charAt(0) !== '*') {
          secs.push(sectionDefaults(fixedToken, cat));
        }
      }
      if (secs.length) {
        var fixedSum = secs.reduce(function (a, s) { return a + s.w; }, 0);
        secs[secs.length - 1].w += W - fixedSum;
      }
      return secs;
    }

    var flexMeta = cat.sections[flexType] || {};
    var flexMax = typeof flexMeta.max === 'number' ? flexMeta.max : Infinity;
    var flexW = W - fixed;
    var flexCount = 1;
    var guard = 0;
    while (flexCount > 0 && (flexW / flexCount) > flexMax && guard < GUARD_MAX) {
      flexCount += 1;
      guard += 1;
    }
    var per = Math.round(flexW / flexCount / 5) * 5;

    var flexIndices = [];
    for (i = 0; i < recipe.length; i++) {
      var tok = recipe[i];
      if (typeof tok !== 'string' || !tok.length) continue;
      if (i === flexTokenIndex) {
        for (var k = 0; k < flexCount; k++) {
          var flexSec = sectionDefaults(flexType, cat);
          flexSec.w = per;
          secs.push(flexSec);
          flexIndices.push(secs.length - 1);
        }
      } else if (tok.charAt(0) !== '*') {
        secs.push(sectionDefaults(tok, cat));
      } else {
        /* A second, distinct flexible token is not used by any layout in this
           catalogue today; fall back to a single fixed-at-default bay so the
           result stays deterministic rather than throwing. */
        secs.push(sectionDefaults(tok.slice(1), cat));
      }
    }

    var sum = secs.reduce(function (a, s) { return a + s.w; }, 0);
    var diff = W - sum;
    if (flexIndices.length) {
      secs[flexIndices[flexIndices.length - 1]].w += diff;
    } else if (secs.length) {
      secs[secs.length - 1].w += diff;
    }
    return secs;
  }

  /* ---------------------------------------------------------------------
     layoutIsValid / availableLayouts — a layout is offered only when W is in
     its range AND every realised section lands within its catalogue min/max,
     so the customer can never pick a layout that would fall over.
  --------------------------------------------------------------------- */
  function layoutIsValid(layout, W, cat) {
    if (!layout || !cat || !cat.sections) return false;
    W = num(W, 0);
    if (typeof layout.minWidth === 'number' && W < layout.minWidth) return false;
    if (typeof layout.maxWidth === 'number' && W > layout.maxWidth) return false;

    var secs;
    try {
      secs = realiseLayout(layout.recipe, W, cat);
    } catch (e) {
      return false;
    }
    if (!secs.length) return false;

    for (var i = 0; i < secs.length; i++) {
      var meta = cat.sections[secs[i].t];
      if (!meta) return false;
      if (secs[i].w < meta.min || secs[i].w > meta.max) return false;
    }
    return true;
  }

  function availableLayouts(W, cat) {
    if (!cat || !Array.isArray(cat.layouts)) return [];
    return cat.layouts.filter(function (layout) {
      return layoutIsValid(layout, W, cat);
    });
  }

  /* ---------------------------------------------------------------------
     midSupportFlags(sections, cat) -> { midSupport }
     Internal flag only (a single/double bay wider than its catalogue
     midSupportOver) — never surfaced to the customer.
  --------------------------------------------------------------------- */
  function midSupportFlags(sections, cat) {
    var midSupport = false;
    if (Array.isArray(sections) && cat && cat.sections) {
      for (var i = 0; i < sections.length; i++) {
        var s = sections[i];
        var meta = s && cat.sections[s.t];
        if (meta && typeof meta.midSupportOver === 'number' && s.w > meta.midSupportOver) {
          midSupport = true;
          break;
        }
      }
    }
    return { midSupport: midSupport };
  }

  /* ---------------------------------------------------------------------
     describe(state, cat) -> plain-English spec string. Robust for any
     combination of state — never throws, never emits "undefined"/"NaN".
  --------------------------------------------------------------------- */
  function capitalise(str) {
    return str && str.length ? str.charAt(0).toUpperCase() + str.slice(1) : str;
  }

  function sectionNoun(type) {
    return SECTION_NOUNS[type] || (type || 'section');
  }

  function summariseMiddle(middle) {
    var types = [];
    middle.forEach(function (s) {
      if (types.indexOf(s.t) === -1) types.push(s.t);
    });
    if (types.length <= 1) {
      return sectionNoun(types[0]) + ' in the middle';
    }
    return types.map(sectionNoun).join(' and ') + ' in the middle';
  }

  function describeSections(sections) {
    if (!Array.isArray(sections) || !sections.length) return '';
    if (sections.length === 1) {
      return capitalise(sectionNoun(sections[0].t)) + ' across the full width.';
    }
    var first = sections[0];
    var last = sections[sections.length - 1];
    var middle = sections.slice(1, -1);
    if (first.t === last.t) {
      var endWord = capitalise(sectionNoun(first.t)) + ' both ends';
      if (!middle.length) return endWord + '.';
      return endWord + ', ' + summariseMiddle(middle) + '.';
    }
    return capitalise(sections.map(function (s) { return sectionNoun(s.t); }).join(', then ')) + '.';
  }

  function describe(state, cat) {
    var parts = [];
    var opening = (state && state.opening) || {};
    var W = opening.w;
    var H = opening.h;
    if (typeof W === 'number' && typeof H === 'number' && isFinite(W) && isFinite(H)) {
      parts.push(W + 'mm wide, ' + H + ' high.');
    }

    var doors = (state && state.doors) || {};
    var doorCat = (cat && cat.doors) || {};
    if (doors.type && doors.type !== 'none') {
      var typeOpt = findOption(doorCat.types, doors.type);
      var insertOpt = findOption(doorCat.inserts, doors.insert);
      var frameOpt = findOption(doorCat.frames, doors.frame);
      var geo = doorGeom(W, H, doors.type, doors.count);
      var n = geo && geo.n ? geo.n : 0;
      var typeLabel = (typeOpt && typeOpt.label ? typeOpt.label : String(doors.type)).toLowerCase();
      var insertLabel = (insertOpt && insertOpt.label ? insertOpt.label : String(doors.insert || '')).toLowerCase();
      var sentence = (n ? n + ' ' : '') + (insertLabel ? insertLabel + ' ' : '') + typeLabel + ' panel' + (n === 1 ? '' : 's');
      sentence = sentence.replace(/\s+/g, ' ').trim() + '.';
      if (frameOpt && frameOpt.label) {
        sentence = sentence.slice(0, -1) + ', ' + frameOpt.label.toLowerCase() + ' frame.';
      }
      parts.push(sentence);
    } else if (doors.type === 'none') {
      parts.push('No doors.');
    }

    var sections = (state && state.internals && state.internals.sections) || [];
    var secText = describeSections(sections);
    if (secText) parts.push(secText);

    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  window.WD_GEOM = {
    doorGeom: doorGeom,
    bandCount: bandCount,
    allowedDoorCounts: allowedDoorCounts,
    realiseLayout: realiseLayout,
    layoutIsValid: layoutIsValid,
    availableLayouts: availableLayouts,
    midSupportFlags: midSupportFlags,
    describe: describe
  };
})();
